import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { toast } from 'sonner';
import { 
  MessageSquare, Send, Inbox, SendHorizontal, Users, Search, 
  CheckCircle, Circle, ChevronRight, ArrowLeft, RefreshCcw,
  Megaphone, User, Clock, Mail, Bell
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function MessagingCenter() {
  const [activeTab, setActiveTab] = useState('inbox');
  const [conversations, setConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [unreadCount, setUnreadCount] = useState(0);
  
  // Compose states
  const [showCompose, setShowCompose] = useState(false);
  const [messageType, setMessageType] = useState('direct'); // direct, group, broadcast
  const [recipientSearch, setRecipientSearch] = useState('');
  const [recipients, setRecipients] = useState([]);
  const [selectedRecipient, setSelectedRecipient] = useState(null);
  const [courses, setCourses] = useState([]);
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [subject, setSubject] = useState('');
  const [content, setContent] = useState('');
  const [replyContent, setReplyContent] = useState('');
  const [sending, setSending] = useState(false);

  const token = localStorage.getItem('token');

  const fetchConversations = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/messages/conversations`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setConversations(data);
      }
    } catch (error) {
      console.error('Error fetching conversations:', error);
    }
  }, [token]);

  const fetchUnreadCount = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/messages/unread-count`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setUnreadCount(data.unread_count);
      }
    } catch (error) {
      console.error('Error fetching unread count:', error);
    }
  }, [token]);

  useEffect(() => {
    const loadData = async () => {
      setIsLoading(true);
      await Promise.all([fetchConversations(), fetchUnreadCount()]);
      setIsLoading(false);
    };
    loadData();

    // Poll for new messages every 30 seconds
    const interval = setInterval(() => {
      fetchUnreadCount();
      if (!selectedConversation) {
        fetchConversations();
      }
    }, 30000);

    return () => clearInterval(interval);
  }, [fetchConversations, fetchUnreadCount, selectedConversation]);

  const searchRecipients = async (type) => {
    if (recipientSearch.length < 2) {
      setRecipients([]);
      return;
    }
    
    try {
      const endpoint = type === 'student' 
        ? `/api/messages/recipients/students?search=${encodeURIComponent(recipientSearch)}`
        : `/api/messages/recipients/staff?search=${encodeURIComponent(recipientSearch)}`;
      
      const res = await fetch(`${API_URL}${endpoint}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setRecipients(data);
      }
    } catch (error) {
      console.error('Error searching recipients:', error);
    }
  };

  const fetchCourses = async () => {
    try {
      const res = await fetch(`${API_URL}/api/messages/courses`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setCourses(data);
      }
    } catch (error) {
      console.error('Error fetching courses:', error);
    }
  };

  const openConversation = async (conversationId) => {
    try {
      const res = await fetch(`${API_URL}/api/messages/conversation/${conversationId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setSelectedConversation(data.conversation);
        setMessages(data.messages);
        // Refresh conversations to update unread count
        fetchConversations();
        fetchUnreadCount();
      }
    } catch (error) {
      toast.error('Failed to load conversation');
    }
  };

  const sendMessage = async () => {
    if (!subject.trim() || !content.trim()) {
      toast.error('Subject and message are required');
      return;
    }

    setSending(true);
    try {
      let payload = {
        subject,
        content,
        message_type: messageType,
        recipient_type: messageType === 'broadcast' ? 'broadcast' : 
                       messageType === 'group' ? 'course_group' : 
                       selectedRecipient?.type || 'student',
        recipient_id: messageType === 'broadcast' ? 'broadcast' :
                     messageType === 'group' ? selectedCourse?.id :
                     selectedRecipient?.id
      };

      const res = await fetch(`${API_URL}/api/messages/send`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      if (res.ok) {
        const data = await res.json();
        toast.success(`Message sent successfully! ${data.recipients_count ? `(${data.recipients_count} recipients)` : ''}`);
        setShowCompose(false);
        resetCompose();
        fetchConversations();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to send message');
      }
    } catch (error) {
      toast.error('Failed to send message');
    } finally {
      setSending(false);
    }
  };

  const sendReply = async () => {
    if (!replyContent.trim() || !selectedConversation) return;

    setSending(true);
    try {
      const res = await fetch(`${API_URL}/api/messages/reply/${selectedConversation.id}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ content: replyContent })
      });

      if (res.ok) {
        setReplyContent('');
        // Refresh messages
        openConversation(selectedConversation.id);
        toast.success('Reply sent');
      } else {
        toast.error('Failed to send reply');
      }
    } catch (error) {
      toast.error('Failed to send reply');
    } finally {
      setSending(false);
    }
  };

  const resetCompose = () => {
    setMessageType('direct');
    setRecipientSearch('');
    setRecipients([]);
    setSelectedRecipient(null);
    setSelectedCourse(null);
    setSubject('');
    setContent('');
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    const now = new Date();
    const diff = now - date;
    
    if (diff < 60000) return 'Just now';
    if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
    if (diff < 86400000) return `${Math.floor(diff / 3600000)}h ago`;
    return date.toLocaleDateString();
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCcw className="animate-spin h-8 w-8 text-indigo-600" />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <MessageSquare className="text-indigo-600" />
            Messaging Center
          </h1>
          <p className="text-gray-500 mt-1">Communicate with students and staff</p>
        </div>
        <div className="flex items-center gap-3">
          {unreadCount > 0 && (
            <span className="bg-red-500 text-white px-3 py-1 rounded-full text-sm font-medium flex items-center gap-1">
              <Bell size={14} />
              {unreadCount} unread
            </span>
          )}
          <Button onClick={() => { setShowCompose(true); fetchCourses(); }} className="bg-indigo-600 hover:bg-indigo-700">
            <Send size={16} className="mr-2" />
            Compose
          </Button>
        </div>
      </div>

      {/* Compose Modal */}
      {showCompose && (
        <Card className="border-2 border-indigo-200">
          <CardHeader className="bg-indigo-50">
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg flex items-center gap-2">
                <Send className="text-indigo-600" size={20} />
                New Message
              </CardTitle>
              <Button variant="ghost" size="sm" onClick={() => { setShowCompose(false); resetCompose(); }}>
                ✕
              </Button>
            </div>
          </CardHeader>
          <CardContent className="p-4 space-y-4">
            {/* Message Type Selection */}
            <div className="flex gap-2">
              <Button 
                variant={messageType === 'direct' ? 'default' : 'outline'}
                size="sm"
                onClick={() => { setMessageType('direct'); setSelectedCourse(null); }}
                className={messageType === 'direct' ? 'bg-indigo-600' : ''}
              >
                <User size={14} className="mr-1" /> Direct
              </Button>
              <Button 
                variant={messageType === 'group' ? 'default' : 'outline'}
                size="sm"
                onClick={() => { setMessageType('group'); setSelectedRecipient(null); }}
                className={messageType === 'group' ? 'bg-indigo-600' : ''}
              >
                <Users size={14} className="mr-1" /> Course Group
              </Button>
              <Button 
                variant={messageType === 'broadcast' ? 'default' : 'outline'}
                size="sm"
                onClick={() => { setMessageType('broadcast'); setSelectedRecipient(null); setSelectedCourse(null); }}
                className={messageType === 'broadcast' ? 'bg-indigo-600' : ''}
              >
                <Megaphone size={14} className="mr-1" /> Broadcast
              </Button>
            </div>

            {/* Recipient Selection */}
            {messageType === 'direct' && (
              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700">Recipient</label>
                <div className="flex gap-2">
                  <div className="relative flex-1">
                    <Search className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
                    <Input
                      placeholder="Search student or staff..."
                      value={recipientSearch}
                      onChange={(e) => setRecipientSearch(e.target.value)}
                      onKeyUp={(e) => {
                        if (e.key === 'Enter' || recipientSearch.length >= 2) {
                          searchRecipients('student');
                        }
                      }}
                      className="pl-10"
                    />
                  </div>
                  <Button variant="outline" size="sm" onClick={() => searchRecipients('student')}>
                    Students
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => searchRecipients('staff')}>
                    Staff
                  </Button>
                </div>
                
                {recipients.length > 0 && (
                  <div className="border rounded-lg max-h-40 overflow-y-auto">
                    {recipients.map(r => (
                      <div 
                        key={r.id}
                        onClick={() => { setSelectedRecipient({ ...r, type: r.student_id ? 'student' : 'staff' }); setRecipients([]); }}
                        className={`p-2 hover:bg-gray-50 cursor-pointer flex items-center justify-between ${selectedRecipient?.id === r.id ? 'bg-indigo-50' : ''}`}
                      >
                        <div>
                          <p className="font-medium text-sm">{r.name}</p>
                          <p className="text-xs text-gray-500">{r.student_id || r.employee_id}</p>
                        </div>
                        {r.course_name && <span className="text-xs bg-gray-100 px-2 py-1 rounded">{r.course_name}</span>}
                      </div>
                    ))}
                  </div>
                )}
                
                {selectedRecipient && (
                  <div className="bg-indigo-50 p-2 rounded flex items-center justify-between">
                    <span className="text-sm font-medium">{selectedRecipient.name}</span>
                    <Button variant="ghost" size="sm" onClick={() => setSelectedRecipient(null)}>✕</Button>
                  </div>
                )}
              </div>
            )}

            {messageType === 'group' && (
              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700">Select Course</label>
                <div className="grid grid-cols-2 gap-2 max-h-40 overflow-y-auto">
                  {courses.map(course => (
                    <div
                      key={course.id}
                      onClick={() => setSelectedCourse(course)}
                      className={`p-3 border rounded-lg cursor-pointer ${selectedCourse?.id === course.id ? 'border-indigo-500 bg-indigo-50' : 'hover:border-gray-400'}`}
                    >
                      <p className="font-medium text-sm">{course.name}</p>
                      <p className="text-xs text-gray-500">{course.student_count} students</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {messageType === 'broadcast' && (
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                <p className="text-sm text-yellow-800 flex items-center gap-2">
                  <Megaphone size={16} />
                  This message will be sent to ALL students
                </p>
              </div>
            )}

            {/* Subject & Content */}
            <div>
              <label className="text-sm font-medium text-gray-700">Subject</label>
              <Input
                placeholder="Message subject..."
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                className="mt-1"
              />
            </div>
            
            <div>
              <label className="text-sm font-medium text-gray-700">Message</label>
              <textarea
                placeholder="Write your message..."
                value={content}
                onChange={(e) => setContent(e.target.value)}
                rows={4}
                className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
            </div>

            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => { setShowCompose(false); resetCompose(); }}>
                Cancel
              </Button>
              <Button 
                onClick={sendMessage} 
                disabled={sending || !subject || !content || (messageType === 'direct' && !selectedRecipient) || (messageType === 'group' && !selectedCourse)}
                className="bg-indigo-600 hover:bg-indigo-700"
              >
                {sending ? <RefreshCcw className="animate-spin mr-2" size={16} /> : <Send size={16} className="mr-2" />}
                Send Message
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Main Content */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Conversations List */}
        <Card className="lg:col-span-1">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg flex items-center gap-2">
              <Inbox size={20} />
              Conversations
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {conversations.length === 0 ? (
              <div className="p-6 text-center text-gray-500">
                <MessageSquare className="mx-auto h-12 w-12 text-gray-300 mb-2" />
                <p>No conversations yet</p>
              </div>
            ) : (
              <div className="divide-y max-h-[500px] overflow-y-auto">
                {conversations.map(conv => (
                  <div
                    key={conv.id}
                    onClick={() => openConversation(conv.id)}
                    className={`p-4 cursor-pointer hover:bg-gray-50 ${selectedConversation?.id === conv.id ? 'bg-indigo-50 border-l-4 border-indigo-500' : ''}`}
                  >
                    <div className="flex items-center justify-between mb-1">
                      <span className="font-medium text-sm text-gray-900">
                        {conv.participant_1_name === localStorage.getItem('userName') ? conv.participant_2_name : conv.participant_1_name}
                      </span>
                      {conv.unread_count > 0 && (
                        <span className="bg-red-500 text-white text-xs px-2 py-0.5 rounded-full">
                          {conv.unread_count}
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-gray-500 truncate">{conv.last_message}</p>
                    <p className="text-xs text-gray-400 mt-1">{formatDate(conv.last_message_at)}</p>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Messages View */}
        <Card className="lg:col-span-2">
          {selectedConversation ? (
            <>
              <CardHeader className="border-b bg-gray-50">
                <div className="flex items-center gap-3">
                  <Button variant="ghost" size="sm" onClick={() => setSelectedConversation(null)}>
                    <ArrowLeft size={16} />
                  </Button>
                  <div>
                    <CardTitle className="text-lg">
                      {selectedConversation.participant_1_name === localStorage.getItem('userName') 
                        ? selectedConversation.participant_2_name 
                        : selectedConversation.participant_1_name}
                    </CardTitle>
                    <CardDescription>
                      {selectedConversation.participant_1_type === 'student' || selectedConversation.participant_2_type === 'student' 
                        ? 'Student' : 'Staff'}
                    </CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="p-0">
                {/* Messages */}
                <div className="h-[350px] overflow-y-auto p-4 space-y-4">
                  {messages.map(msg => {
                    const isMe = msg.sender_name === localStorage.getItem('userName');
                    return (
                      <div key={msg.id} className={`flex ${isMe ? 'justify-end' : 'justify-start'}`}>
                        <div className={`max-w-[70%] rounded-lg p-3 ${isMe ? 'bg-indigo-600 text-white' : 'bg-gray-100'}`}>
                          <p className={`text-xs mb-1 ${isMe ? 'text-indigo-200' : 'text-gray-500'}`}>
                            {msg.sender_name} • {formatDate(msg.created_at)}
                          </p>
                          {msg.subject && msg.subject !== 'Re: Conversation' && (
                            <p className={`font-medium text-sm mb-1 ${isMe ? 'text-white' : 'text-gray-800'}`}>
                              {msg.subject}
                            </p>
                          )}
                          <p className={`text-sm ${isMe ? 'text-white' : 'text-gray-700'}`}>{msg.content}</p>
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Reply Input */}
                <div className="border-t p-4 flex gap-2">
                  <Input
                    placeholder="Type your reply..."
                    value={replyContent}
                    onChange={(e) => setReplyContent(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && sendReply()}
                    className="flex-1"
                  />
                  <Button 
                    onClick={sendReply} 
                    disabled={sending || !replyContent.trim()}
                    className="bg-indigo-600 hover:bg-indigo-700"
                  >
                    {sending ? <RefreshCcw className="animate-spin" size={16} /> : <Send size={16} />}
                  </Button>
                </div>
              </CardContent>
            </>
          ) : (
            <div className="flex items-center justify-center h-[450px] text-gray-400">
              <div className="text-center">
                <Mail className="mx-auto h-16 w-16 text-gray-300 mb-4" />
                <p className="text-lg font-medium">Select a conversation</p>
                <p className="text-sm">or compose a new message</p>
              </div>
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}
